export type ServiceDetail = {
  label: string;
  whatIs: string;
  whyItMatters: string;
  howYouBenefit: string;
  offerings: string[];
  howCustomersBenefit: string;
  whatToExpect?: string[];
  ctaLink: string;
  ctaLabel: string;
};

export const serviceDetails: Record<string, ServiceDetail> = {
  seo: {
    label: "Semantic SEO & AEO",
    whatIs:
      "Semantic SEO & Answer Engine Optimization goes beyond basic keywords. We develop comprehensive topic clusters and entity relationships so your content is recognized as an authoritative source by both traditional search engines and AI platforms like ChatGPT, Gemini, and Perplexity.",
    whyItMatters:
      "Search intent is increasingly conversational and AI-driven. Content optimized only for classic SEO often fails to appear in AI-generated answers, limiting your reach as more users rely on generative tools.",
    howYouBenefit:
      "Your content ranks higher in Google, gets frequently cited by AI engines, builds long-term topical authority, and drives consistent organic growth with reduced dependency on single keywords.",
    offerings: [
      "Answer Engine Optimization (AEO) tailored for major AI platforms",
      "Topical maps and entity coverage",
      "Technical SEO audits with Core Web Vitals optimization",
      "Programmatic SEO for scalable content production",
    ],
    howCustomersBenefit:
      "Your content ranks higher in Google, appears frequently in ChatGPT/Gemini/Perplexity answers, attracts consistent qualified traffic, and reduces reliance on fragile single-keyword rankings. Expect 30-100%+ organic traffic growth within 6-18 months.",
    ctaLink: "/services/seo",
    ctaLabel: "Explore Semantic SEO & AEO",
  },
  aeo: {
    label: "Semantic SEO & AEO",
    whatIs:
      "Semantic SEO & Answer Engine Optimization goes beyond basic keywords. We develop comprehensive topic clusters and entity relationships so your content is recognized as an authoritative source by both traditional search engines and AI platforms like ChatGPT, Gemini, and Perplexity.",
    whyItMatters:
      "Search intent is increasingly conversational and AI-driven. Content optimized only for classic SEO often fails to appear in AI-generated answers, limiting your reach as more users rely on generative tools.",
    howYouBenefit:
      "Your content ranks higher in Google, gets frequently cited by AI engines, builds long-term topical authority, and drives consistent organic growth with reduced dependency on single keywords.",
    offerings: [
      "Answer Engine Optimization (AEO) tailored for major AI platforms",
      "Topical maps and entity coverage",
      "Technical SEO audits with Core Web Vitals optimization",
      "Programmatic SEO for scalable content production",
    ],
    howCustomersBenefit:
      "Your content ranks higher in Google, appears frequently in ChatGPT/Gemini/Perplexity answers, attracts consistent qualified traffic, and reduces reliance on fragile single-keyword rankings. Expect 30-100%+ organic traffic growth within 6-18 months.",
    ctaLink: "/services/aeo",
    ctaLabel: "Explore Semantic SEO & AEO",
  },
  content: {
    label: "Content Strategy",
    whatIs:
      "We create and manage thought leadership content including detailed editorial calendars, SME-driven articles, long-form guides, whitepapers, and downloadable resources.",
    whyItMatters:
      "Modern B2B and high-consideration buyers extensively research before purchasing. Generic content fails to engage them or build trust.",
    howYouBenefit:
      "Position your brand as an industry leader, attract highly qualified leads, improve time-on-page and engagement metrics, and create assets that support sales and marketing efforts for years.",
    offerings: [
      "Editorial calendar and overarching narrative strategy",
      "SME interviews and professional ghostwriting",
      "Long-form articles (2,000–6,000 words) optimized for SEO and readability",
      "Whitepapers, eBooks, and in-depth reports for lead generation",
    ],
    howCustomersBenefit:
      "You position as the go-to expert in your industry, generate high-quality leads from gated assets, increase engagement and time-on-site, and create evergreen content that continues driving results for years.",
    ctaLink: "/services/content",
    ctaLabel: "Explore Content Strategy",
  },
  smo: {
    label: "Social (SMO)",
    whatIs:
      "We break down your core long-form content into platform-optimized pieces and run targeted campaigns across LinkedIn, X, and relevant industry communities.",
    whyItMatters:
      "Social platforms serve as major content discovery engines and help amplify reach while driving qualified traffic back to your website.",
    howYouBenefit:
      "Expanded audience reach, stronger community engagement, faster content distribution, growth in newsletter subscribers, and improved conversion from social channels.",
    offerings: [
      "LinkedIn executive presence and thought leadership amplification",
      "Narrative atomization for different platforms",
      "Community building and newsletter growth strategies",
      "Automated yet personalized content-to-social pipelines",
    ],
    howCustomersBenefit:
      "You reach larger targeted audiences, grow engaged communities and newsletter lists, drive qualified traffic to your site, and convert social followers into leads and customers more effectively.",
    ctaLink: "/services/smo",
    ctaLabel: "Explore Social Media Optimization",
  },
  optimization: {
    label: "Content Optimization",
    whatIs:
      "We audit, update, and re-optimize existing content to improve rankings, revive traffic, and increase conversions from your current asset library.",
    whyItMatters:
      "Many websites have valuable older content that is losing traffic due to outdated information, weak optimization, or changing search intent.",
    howYouBenefit:
      "Quick wins in traffic and rankings with lower investment than new content creation, higher conversion rates, improved site-wide authority, and maximized ROI on past content efforts.",
    offerings: [
      "Content refresh and re-optimization programs",
      "Strategic internal linking architecture",
      "On-page conversion rate optimization",
      "Optimization for SERP features and rich results",
    ],
    howCustomersBenefit:
      "You recover lost traffic quickly and cost-effectively, improve conversion rates on existing pages, boost overall site authority, and achieve faster results than creating everything from scratch.",
    ctaLink: "/services/optimization",
    ctaLabel: "Explore Content Optimization",
  },
  linkbuilding: {
    label: "Link Building",
    whatIs:
      "We run data-driven PR campaigns, secure expert placements, and earn high-quality backlinks from reputable publications to strengthen domain authority.",
    whyItMatters:
      "Authoritative backlinks remain one of the strongest signals for search rankings and brand credibility.",
    howYouBenefit:
      "Improved domain rating and search rankings, referral traffic from trusted sources, enhanced brand reputation, and a stronger foundation for all other digital marketing efforts.",
    offerings: [
      "Data-driven digital PR campaigns",
      "HARO and Qwoted expert placement services",
      "Guest posting on high-DR (60+) websites",
      "Broken link reclamation and recovery campaigns",
    ],
    howCustomersBenefit:
      "Your domain authority rises steadily, you earn referral traffic from reputable sites, rankings improve across the board, and your brand gains lasting credibility that supports all marketing efforts.",
    whatToExpect: [
      "Customized digital marketing strategy aligned with your goals",
      "Regular production and promotion of high-quality assets",
      "Transparent monthly reporting on traffic, rankings, leads, and ROI",
      "Continuous optimization and AI-readiness updates",
      "End-to-end execution with dedicated strategy support",
    ],
    ctaLink: "/services/linkbuilding",
    ctaLabel: "Explore Link Building",
  },
};
